import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography, makeStyles } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { closeViewer, openAssetViewer } from '../../../store/contentAssetViewer/ViewerActions'
import { CONTENT_ASSET_TYPE, getTypeIcon } from '../resources/ContentAssetTypeIcons'

const ERROR_MESSAGE = "Couldn't load resource. The resource may have been removed or you may not have access."

const useStyles = makeStyles((theme) => ({
	titleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		paddingRight: theme.spacing(6) // make room for the close button
	},
	titleIcon: {
		color: theme.palette.colors && theme.palette.colors.purple ? theme.palette.colors.purple : theme.palette.primary.main
	},
	titleText: {
		flex: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap'
	},
	closeButton: {
		position: 'absolute',
		right: theme.spacing(1),
		top: theme.spacing(1)
	},
	content: {
		minHeight: 320,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		justifyContent: 'center'
	},
	loadingState: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: 320
	},
	errorState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		minHeight: 320,
		gap: theme.spacing(2),
		color: theme.palette.text.secondary,
		textAlign: 'center'
	},
	iframe: {
		border: 'none',
		width: '100%',
		height: '70vh'
	},
	image: {
		maxWidth: '100%',
		maxHeight: '70vh',
		display: 'block',
		margin: '0 auto',
		userSelect: 'none'
	},
	video: {
		maxWidth: '100%',
		maxHeight: '70vh',
		display: 'block',
		margin: '0 auto'
	},
	unsupported: {
		textAlign: 'center',
		color: theme.palette.text.secondary,
		padding: theme.spacing(4)
	}
}))

const ContentAssetViewerModal = () => {
	const classes = useStyles()
	const dispatch = useDispatch()

	const isOpen = useSelector((state) => state.contentAssetViewer.get('isOpen'))
	const isLoading = useSelector((state) => state.contentAssetViewer.get('isLoading'))
	const error = useSelector((state) => state.contentAssetViewer.get('error'))
	const blobUrl = useSelector((state) => state.contentAssetViewer.get('blobUrl'))
	const assetImmutable = useSelector((state) => state.contentAssetViewer.get('asset'))
	const asset = assetImmutable && assetImmutable.toJS ? assetImmutable.toJS() : null

	useEffect(() => {
		if (!blobUrl) return
		return () => URL.revokeObjectURL(blobUrl)
	}, [blobUrl])

	const handleClose = () => dispatch(closeViewer())
	const handleRetry = () => {
		if (asset) dispatch(openAssetViewer(asset))
	}

	const renderBody = () => {
		if (isLoading) {
			return (
				<div className={classes.loadingState}>
					<CircularProgress />
				</div>
			)
		}
		if (error) {
			return (
				<div className={classes.errorState}>
					<Typography variant='body1'>{ERROR_MESSAGE}</Typography>
				</div>
			)
		}
		if (!asset || !blobUrl) {
			return null
		}

		switch (asset.Type) {
			case CONTENT_ASSET_TYPE.DOCUMENT:
				return <iframe className={classes.iframe} src={`${blobUrl}#toolbar=0`} title={asset.Title || 'Resource preview'} />
			case CONTENT_ASSET_TYPE.IMAGE:
				return (
					<img
						className={classes.image}
						src={blobUrl}
						alt={asset.Title || 'Resource preview'}
						draggable={false}
						onContextMenu={(e) => e.preventDefault()}
					/>
				)
			case CONTENT_ASSET_TYPE.VIDEO:
				return (
					<video
						className={classes.video}
						src={blobUrl}
						controls
						controlsList='nodownload'
						disablePictureInPicture
						onContextMenu={(e) => e.preventDefault()}
						aria-label={asset.Title || 'Resource preview'}
					/>
				)
			default:
				return (
					<Typography variant='body2' className={classes.unsupported}>
						Preview not available for this resource type.
					</Typography>
				)
		}
	}

	const TitleIcon = asset ? getTypeIcon(asset.Type) : null

	return (
		<Dialog open={isOpen} onClose={handleClose} maxWidth='lg' fullWidth>
			<DialogTitle disableTypography>
				<div className={classes.titleRow}>
					{TitleIcon && <TitleIcon className={classes.titleIcon} />}
					<Typography variant='h6' component='span' className={classes.titleText}>
						{asset ? asset.Title : ''}
					</Typography>
				</div>
				<IconButton aria-label='close' className={classes.closeButton} onClick={handleClose} size='small'>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers className={classes.content}>
				{renderBody()}
			</DialogContent>
			<DialogActions>
				{error && (
					<Button color='primary' onClick={handleRetry}>
						Retry
					</Button>
				)}
				<Button onClick={handleClose}>Close</Button>
			</DialogActions>
		</Dialog>
	)
}

export default ContentAssetViewerModal
